import fulfill from '../../fulfill';
import { CORE_API_HOSTNAME } from '../../env';
import { SideEffectFunction } from '../../sideEffects';
import {
    ActionTypes,
    CreateCampaign,
    CreateCampaignResponse,
    CreateAsset,
    CreateAssetResponse,
    UploadAssetResponse,
    FetchLocations,
    FetchLocationsResponse,
    FetchProductsCatalogs,
    FetchProductsCatalogsResponse,
    FetchProducts,
    FetchProductsResponse,
    FetchDiscount,
    FetchDiscountResponse
} from './actions';
import { AssetResponseObject, Product, ProductCatalog } from '../types';
import { Discount, Campaign } from '../../types/discounts';
import { SalesLocation } from '../../types/customers';

export const createCampaign: SideEffectFunction<CreateCampaign, CreateCampaignResponse> = async (action) => {
    const { accountId, discounts, campaign, customers } = action.payload;
    const result: CreateCampaignResponse = await fulfill.post({
        accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${accountId}/discounts/campaigns`,
        json: campaign,
        handlers: {
            200: (campaign: Campaign) => ({
                type: ActionTypes.CreateCampaignResponse,
                payload: {
                    campaign,
                    discounts,
                    accountId,
                    customers,
                },
                meta: {
                    cause: action,
                },
            }),
        },
    });
    const createDiscountPromises = discounts.map((discount) => {
        return createDiscountsAndAddToCustomers(accountId, discount, customers);
    });
    await Promise.all(createDiscountPromises);
    return result;
};

const createDiscountsAndAddToCustomers = async (accountId: string, discount: Discount, customers: string[]) => {
    const result = await fulfill.post({
        accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${accountId}/discounts/rules`,
        json: discount,
        handlers: {
            200: (createdDiscount: Discount) => createdDiscount,
        },
    });
    const discountId = result.id;
    await fulfill.post({
        accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${accountId}/discounts/rules/${discountId}/customers`,
        json: {
            customers,
        },
        handlers: {
            200: () => {},
        },
    });
};

export const createAsset: SideEffectFunction<CreateAsset, CreateAssetResponse> = async (action) => {
    const { accountId, asset } = action.payload;
    const result: CreateAssetResponse = await fulfill.post({
        accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${accountId}/management/assets`,
        json: {
            size_bytes: asset.file.size,
            mime_type: asset.file.type,
        },
        handlers: {
            200: (assetDetails: AssetResponseObject) => ({
                type: ActionTypes.CreateAssetResponse,
                payload: {
                    accountId,
                    uploadUrl: assetDetails.upload_url,
                    publicUrl: assetDetails.public_url,
                    type: asset.file.type,
                    size: asset.file.size,
                    file: asset.file,
                    discountId: asset.discountId,
                },
                meta: {
                    cause: action,
                },
            }),
        },
    });
    return result;
};

export const uploadAsset: SideEffectFunction<CreateAssetResponse, UploadAssetResponse> = async (action) => {
    const { accountId, uploadUrl, file } = action.payload;
    const result: UploadAssetResponse = await fulfill.put({
        accountId,
        noAuthentication: true,
        url: uploadUrl,
        body: file,
        handlers: {
            200: () => ({
                type: ActionTypes.UploadAssetResponse,
                meta: {
                    cause: action,
                },
            }),
        },
        cacheBuster: false,
    });
    return result;
};

export const fetchLocations: SideEffectFunction<FetchLocations, FetchLocationsResponse> = async (action) => {
    const { accountId, search } = action.payload;
    const result: FetchLocationsResponse = await fulfill.get({
        accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${accountId}/locations/?limit=100${search ? '&search=' + search : ''}`,
        handlers: {
            200: (locations: SalesLocation[]) => ({
                type: ActionTypes.FetchLocationsResponse,
                payload: locations,
                meta: {
                    cause: action,
                },
            }),
        },
    });
    return result;
};

export const fetchProductsCatalogs: SideEffectFunction<FetchProductsCatalogs, FetchProductsCatalogsResponse> = async (
    action
) => {
    const { accountId } = action.payload;
    const result: FetchProductsCatalogsResponse = await fulfill.get({
        accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${accountId}/products/catalogs`,
        headers: {
            'Content-Type': 'application/json',
        },
        handlers: {
            200: (response: { catalogs: ProductCatalog[] }) => ({
                type: ActionTypes.FetchProductsCatalogsResponse,
                payload: response.catalogs,
                meta: {
                    cause: action,
                },
            }),
            404: (_: { catalogs: ProductCatalog[] }) => ({
                type: ActionTypes.FetchProductsCatalogsResponse,
                payload: [],
                meta: {
                    cause: action,
                },
            }),
        },
    });
    return result;
};

export const fetchProducts: SideEffectFunction<FetchProducts, FetchProductsResponse> = async (action) => {
    const { accountId, catalogId } = action.payload;
    const result: FetchProductsResponse = await fulfill.get({
        accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${accountId}/products/catalogs/${catalogId}/products`,
        headers: {
            'Content-Type': 'application/json',
        },
        handlers: {
            200: (response: { products: Product[] }) => ({
                type: ActionTypes.FetchProductsResponse,
                payload: response.products,
                meta: {
                    cause: action,
                },
            }),
            404: (_: { products: Product[] }) => ({
                type: ActionTypes.FetchProductsResponse,
                payload: [],
                meta: {
                    cause: action,
                },
            }),
        },
    });
    return result;
};

export const fetchDiscount: SideEffectFunction<FetchDiscount, FetchDiscountResponse> = async (action) => {
    const { accountId, discountId } = action.payload;
    const result: FetchDiscountResponse = await fulfill.get({
        accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${accountId}/discounts/rules/${discountId}`,
        handlers: {
            200: (discount: Discount) => ({
                type: ActionTypes.FetchDiscountResponse,
                payload: discount,
                meta: {
                    cause: action,
                },
            }),
        },
    });
    return result;
};

const effects = {
    [ActionTypes.CreateCampaign]: createCampaign,
    [ActionTypes.CreateAsset]: createAsset,
    [ActionTypes.CreateAssetResponse]: uploadAsset,
    [ActionTypes.FetchLocations]: fetchLocations,
    [ActionTypes.FetchProductsCatalogs]: fetchProductsCatalogs,
    [ActionTypes.FetchProducts]: fetchProducts,
    [ActionTypes.FetchDiscount]: fetchDiscount,
};

export default effects;
